import { createElement, useCallback, useMemo, useState } from "react";
import { useAuth } from "@/shared/stores/useAuth";
import { useCurrentTalentProfile } from "@/shared";
import { PublishedCard } from "../../types";
import { DeckCardKakaoKind } from "@/modules/kakao";
import DeckCardShareModal from "../modal/DeckCardShareModal";
import { getDemoCardIdFromCard } from "../../utils/app-deck/ownedDeckCardList.utils";

type UseDeckCardTalentExpandShareOptions = {
  cardKind: DeckCardKakaoKind;
  resolveDemoCardId?: (card: PublishedCard) => string | undefined;
};

export const useDeckCardTalentExpandShare = ({
  cardKind,
  resolveDemoCardId = getDemoCardIdFromCard,
}: UseDeckCardTalentExpandShareOptions) => {
  const { userInfo } = useAuth();
  const { currentProfile } = useCurrentTalentProfile();
  const [shareOpen, setShareOpen] = useState(false);
  const [shareCard, setShareCard] = useState<PublishedCard | null>(null);

  const onExpandShare = useCallback((card: PublishedCard) => {
    setShareCard(card);
    setShareOpen(true);
  }, []);

  const closeShare = useCallback(() => {
    setShareOpen(false);
    setShareCard(null);
  }, []);

  const shareMeta = useMemo(() => {
    if (!shareCard) return null;
    const talentName =
      shareCard.talent?.username ??
      currentProfile?.username ??
      currentProfile?.view_name ??
      userInfo?.username ??
      "Creator";

    const coverImageUrl = shareCard.coverImageUrl?.trim() ?? "";

    return {
      talentName,
      cardTitle: shareCard.title ?? talentName,
      coverImageUrl,
      imageUrl: coverImageUrl,
      senderName: userInfo?.username ?? talentName,
      demoCardId:
        cardKind === "demo" ? resolveDemoCardId(shareCard) : undefined,
      talentId:
        shareCard.talent?.id ??
        shareCard.talent?.user_id ??
        currentProfile?.id,
    };
  }, [shareCard, cardKind, currentProfile, userInfo, resolveDemoCardId]);

  const shareModal =
    shareCard && shareMeta
      ? createElement(DeckCardShareModal, {
          open: shareOpen,
          onClose: closeShare,
          cardKind,
          mainCardId: shareCard.id,
          talentId: shareMeta.talentId,
          demoCardId: shareMeta.demoCardId,
          shareMeta,
        })
      : null;

  return {
    onExpandShare,
    shareModal,
    deckCardShareKind: cardKind,
  };
};
